import { database } from './firebase';
import { ref, push, set, onValue, update, remove } from 'firebase/database';

export const ChoferesService = {
  // Crear un nuevo chofer
  async crearChofer(choferData) {
    try {
      // Guardar en Realtime Database
      const choferesRef = ref(database, 'choferes');
      const nuevoChoferRef = push(choferesRef);
      const choferConId = {
        ...choferData,
        id: nuevoChoferRef.key,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      
      await set(nuevoChoferRef, choferConId);
      
      // También guardar en localStorage como respaldo
      this.guardarEnLocalStorage(choferConId);
      
      return nuevoChoferRef.key;
    } catch (error) {
      console.error('Error al crear chofer en Realtime Database:', error);
      
      // Si Firebase falla, guardar solo en localStorage
      const id = 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const choferConId = {
        ...choferData,
        id: id,
        fechaCreacion: new Date().toISOString(),
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      this.guardarEnLocalStorage(choferConId);
      
      return id;
    }
  },

  // Guardar en localStorage
  guardarEnLocalStorage(choferConId) {
    try {
      const choferesExistentes = this.obtenerDeLocalStorage();
      choferesExistentes.push(choferConId);
      localStorage.setItem('choferes_backup', JSON.stringify(choferesExistentes));
    } catch (error) {
      console.error('Error al guardar en localStorage:', error);
    }
  },

  // Obtener de localStorage
  obtenerDeLocalStorage() {
    try {
      const choferes = localStorage.getItem('choferes_backup');
      return choferes ? JSON.parse(choferes) : [];
    } catch (error) {
      console.error('Error al obtener de localStorage:', error);
      return [];
    }
  },

  // Obtener todos los choferes
  async obtenerChoferes() {
    try {
      const choferesRef = ref(database, 'choferes');
      
      return new Promise((resolve, reject) => {
        onValue(choferesRef, (snapshot) => {
          const choferesFirebase = [];
          
          if (snapshot.exists()) {
            snapshot.forEach((child) => {
              choferesFirebase.push(child.val());
            });
          }
          
          // También obtener de localStorage y combinar
          const choferesLocal = this.obtenerDeLocalStorage();
          const choferesCombinados = [...choferesFirebase, ...choferesLocal];
          
          // Eliminar duplicados basándose en el ID
          const choferesUnicos = choferesCombinados.filter((chofer, index, self) => 
            index === self.findIndex(c => c.id === chofer.id)
          );
          
          resolve(choferesUnicos);
        }, reject);
      });
    } catch (error) {
      console.error('Error al obtener choferes de Realtime Database:', error);
      
      // Si Firebase falla, devolver solo de localStorage
      return this.obtenerDeLocalStorage();
    }
  },

  // Actualizar un chofer existente
  async actualizarChofer(id, choferData) {
    try {
      // Actualizar en Realtime Database
      const choferRef = ref(database, `choferes/${id}`);
      const datosActualizados = {
        ...choferData,
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now()
      };
      
      await update(choferRef, datosActualizados);
      
      // También actualizar en localStorage
      this.actualizarEnLocalStorage(id, choferData);
    } catch (error) {
      console.error('Error al actualizar chofer en Realtime Database:', error);
      
      // Si Firebase falla, actualizar solo en localStorage
      this.actualizarEnLocalStorage(id, choferData);
    }
  },

  // Actualizar en localStorage
  actualizarEnLocalStorage(id, choferData) {
    try {
      const choferes = this.obtenerDeLocalStorage();
      const index = choferes.findIndex(c => c.id === id);
      
      if (index !== -1) {
        choferes[index] = {
          ...choferes[index],
          ...choferData,
          fechaActualizacion: new Date()
        };
        localStorage.setItem('choferes_backup', JSON.stringify(choferes));
      }
    } catch (error) {
      console.error('Error al actualizar en localStorage:', error);
    }
  },


  // Eliminar de localStorage
  eliminarDeLocalStorage(id) {
    try {
      const choferes = this.obtenerDeLocalStorage();
      const choferesFiltrados = choferes.filter(c => c.id !== id);
      localStorage.setItem('choferes_backup', JSON.stringify(choferesFiltrados));
    } catch (error) {
      console.error('Error al eliminar de localStorage:', error);
    }
  },

  // Suscribirse a cambios en tiempo real
  suscribirseChoferes(callback) {
    try {
      // Primero intentar obtener datos de localStorage inmediatamente
      const choferesLocal = this.obtenerDeLocalStorage();
      
      // Si hay datos en localStorage, mostrarlos inmediatamente
      if (choferesLocal.length > 0) {
        callback(choferesLocal);
      }
      
      // Conectar con Realtime Database
      const choferesRef = ref(database, 'choferes');
      return onValue(choferesRef, (snapshot) => {
        const choferesFirebase = [];
        
        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            choferesFirebase.push(child.val());
          });
        }
        
        // También obtener de localStorage y combinar
        const choferesLocalActualizados = this.obtenerDeLocalStorage();
        const choferesCombinados = [...choferesFirebase, ...choferesLocalActualizados];
        
        // Eliminar duplicados
        const choferesUnicos = choferesCombinados.filter((chofer, index, self) => 
          index === self.findIndex(c => c.id === chofer.id)
        );
        
        callback(choferesUnicos);
      }, (error) => {
        console.error('Error en suscripción de Realtime Database:', error);
        
        // Si la suscripción falla, usar solo localStorage
        const choferesLocal = this.obtenerDeLocalStorage();
        callback(choferesLocal);
      });
    } catch (error) {
      console.error('Error al suscribirse a choferes:', error);
      
      // Si hay error, usar solo localStorage
      const choferesLocal = this.obtenerDeLocalStorage();
      callback(choferesLocal);
      
      // Devolver una función de limpieza vacía
      return () => {};
    }
  },

  // Obtener chofer por ID
  async obtenerChoferPorId(id) {
    try {
      const choferRef = ref(database, `choferes/${id}`);
      
      return new Promise((resolve, reject) => {
        onValue(choferRef, (snapshot) => {
          if (snapshot.exists()) {
            resolve(snapshot.val());
          } else {
            reject(new Error('Chofer no encontrado'));
          }
        }, reject);
      });
    } catch (error) {
      console.error('Error al obtener chofer por ID:', error);
      throw error;
    }
  },

  // Buscar choferes por criterios
  async buscarChoferes(criterios) {
    try {
      const choferesRef = ref(database, 'choferes');
      
      return new Promise((resolve, reject) => {
        onValue(choferesRef, (snapshot) => {
          let choferes = [];
          
          if (snapshot.exists()) {
            snapshot.forEach((child) => {
              choferes.push(child.val());
            });
          }

          // Aplicar filtros
          if (criterios.nombre) {
            choferes = choferes.filter(c => 
              c.nombre?.toLowerCase().includes(criterios.nombre.toLowerCase())
            );
          }
          if (criterios.apellido) {
            choferes = choferes.filter(c => 
              c.apellido?.toLowerCase().includes(criterios.apellido.toLowerCase())
            );
          }
          if (criterios.estado) {
            choferes = choferes.filter(c => c.estado === criterios.estado);
          }
          if (criterios.tipoLicencia) {
            choferes = choferes.filter(c => c.tipoLicencia === criterios.tipoLicencia);
          }

          resolve(choferes);
        }, reject);
      });
    } catch (error) {
      console.error('Error al buscar choferes:', error);
      throw error;
    }
  },

  // Eliminar chofer
  async eliminarChofer(id) {
    try {
      // Eliminar de Realtime Database
      const choferRef = ref(database, `choferes/${id}`);
      await remove(choferRef);
      
      // También eliminar de localStorage
      this.eliminarDeLocalStorage(id);
    } catch (error) {
      console.error('Error al eliminar chofer de Realtime Database:', error);
      
      // Si Firebase falla, eliminar solo de localStorage
      this.eliminarDeLocalStorage(id);
    }
  }
};